import React, { FC, useMemo, useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay/hooks';
import IfCanAccess from 'src/components/widgets/ifCanAccess/ifCanAccess';
import { T } from 'src/shared/helpers';
import { Mutable } from 'src/shared/types';
import { PrimaryButton } from '../../../../widgets/buttons';
import { PageFooterButtons } from '../../../../widgets/headers';
import { ContainerBase } from '../../../../widgets/layouts';
import { removeNotification, showNotification } from '../../../../widgets/notificationToaster';
import { PageHeader } from '../../../../widgets/pageHeader';
import { useProductConfigurationState } from '../../state';
import { productProvider_retailProduct$data } from '../../state/queries/__generated__/productProvider_retailProduct.graphql';
import { editConfigurationMutation, editConfigurationMutation$data } from './__generated__/editConfigurationMutation.graphql';
import { editConfigurationQuery } from './__generated__/editConfigurationQuery.graphql';
import { getPrices } from './helpers';
import PricesTable from './pricesTable';

export const EditConfiguration: FC<{}> = (props) => {
    const { state } = useProductConfigurationState();

    const query = useLazyLoadQuery<editConfigurationQuery>(
        graphql`
            query editConfigurationQuery($id: String!, $provider: ProductProvider!) {
                retailProduct(id: $id, provider: $provider) {
                    ...productProvider_retailProduct @relay(mask: false)
                }
            }
        `,
        {
            id: state.product.id ?? '',
            provider: state.provider
        }
    );

    const [saveProduct, isSavingProduct] = useMutation<editConfigurationMutation>(graphql`
        mutation editConfigurationMutation($id: String!, $provider: ProductProvider!, $profitPercentage: Int!) {
            setRetailProfit(id: $id, provider: $provider, profitPercentage: $profitPercentage)
        }
    `);

    const variants = query.retailProduct.variants as Mutable<productProvider_retailProduct$data['variants']>;

    const [profitString, setProfitString] = useState(query.retailProduct.profitPercentage.toString());
    const profit = useMemo(() => parseFloat(profitString) || 0, [profitString]);
    const prices = useMemo(() => getPrices(variants, profit), [profit, query.retailProduct.variants]);

    const handleSaveClick = async () => {
        const loadingNotification = showNotification({
            type: 'default',
            title: T._('Saving...', 'Admin'),
            message: T._('Saving product...', 'Admin')
        });

        await new Promise<editConfigurationMutation$data>((resolve, reject) =>
            saveProduct({
                onCompleted: resolve,
                onError: reject,
                variables: {
                    id: state.product.id ?? '',
                    profitPercentage: profit,
                    provider: state.provider
                }
            })
        );

        removeNotification(loadingNotification);
        showNotification({
            type: 'success',
            message: T._('Product saved.', 'Admin'),
            dismiss: {
                duration: 5000
            }
        });
    };

    return (
        <IfCanAccess permission='PRODUCTS_EDIT'>
            <ContainerBase>
                <PageHeader isBackVisible={false} title={T._('Configuration', 'Admin')}></PageHeader>
                <PricesTable prices={prices} profit={profitString} onProfitChange={setProfitString} provider={state.provider} />
            </ContainerBase>
            
            <PageFooterButtons $fixed={true}>
                <PrimaryButton $size='large' disabled={isSavingProduct} onClick={handleSaveClick}>
                    {T._('Save', 'Admin')}
                </PrimaryButton>
            </PageFooterButtons>
        </IfCanAccess>
    );
};
